import { Component, computed, HostListener, Inject, OnDestroy, OnInit, Signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PendoService } from 'analytics-lib';
import { ClientConfigService } from 'client-config-service-lib';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { IComposeMailService, IRecipient } from 'email-service-lib';
import { SectionType } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IEntity, IEntityMetadata, IRemoteFileInfo, ISharedServiceProviderService, IUploadedFileInfo } from 'interfaces-lib';
import {
  IIconService,
  IWorkflowService,
  OutputFilename,
  Recipient,
  SignatureConfig,
  TemplateWorkflowConfig,
  Workflow,
  WorkflowOptionsMessage,
} from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { IRecipientInfoService } from 'recipient-info-service-lib';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ITerminologyService, TerminologySentenceBuilder, TermPlaceholder } from 'terminology-lib';
import { UtilsService } from 'utils-service-lib';
import { EditS3FileTask, WorkflowTaskRunner } from 'workflow-task-service-lib';
import { runtimeEnvironment } from '../../../environments/environment';
import { Adv2ConfigService } from '../../services/adv2-config.service';
import { CurrentUserOverrideStateService } from '../../services/current-user-override-state.service';
import { DocumentSender } from '../../common-components/document-sender-picker/document-sender-picker.component';
import { FileListControllerService } from '../../services/file-list.controller.service';
import { GenerateFilenameService, IGenerateFilenameResponse } from '../../services/generic/generate-filename.service';
import { ServiceProviderService } from '../../services/service.provider.service';
import { SaveToEntityTask } from '../../tasks/save-to-entity-task';
import { SendForSignatureTask, SendStatus } from '../../tasks/send-for-signature-task';
import { EntitySelection } from '../template-workflow-types/templateworkflowcontext';
import { DocusignConsentService, SignatureConsentProgress } from './docusign-consent.service';

export interface IAdActionsDialogData {
  options: WorkflowOptionsMessage;
  templateWorkflowConfig: TemplateWorkflowConfig;
  entity: IEntity;
  entitySelection: EntitySelection;
}

enum DocumentAction {
  Save = 'Save',
  Email = 'Send',
}

@Component({
  selector: 'app-ad-actions-dialog',
  templateUrl: './ad-actions-dialog.component.html',
  styleUrls: ['./ad-actions-dialog.component.scss', './chrome-extension.ad-actions-dialog.component.scss'],
  standalone: false,
})
export class AdActionsDialogComponent implements OnInit, OnDestroy {
  documentType: string;
  editEnvelope: boolean;
  actions = [
    {
      action: DocumentAction.Save,
      id: 'save-to-entity-btn',
      title: 'Save to the Bullhorn entity',
      prefix: 'Save as ',
      tooltip: '',
      icon: 'cloud_upload',
    },
    {
      action: DocumentAction.Email,
      id: 'email-to-btn',
      title: 'Email from Bullhorn',
      prefix: 'Email as ',
      tooltip: '',
      icon: 'email',
    },
  ];

  renderMode = runtimeEnvironment.renderMode;
  openDialog: boolean;
  editDocumentLocation: IRemoteFileInfo;
  showOverlay = false;
  envelopeCreatedTime: string;
  envelopeUrl: string;

  saveToDatasourceText: string;

  invalidRecipientDetails: string;
  validConfig = false;
  actionInProgress = false;

  formGroup: UntypedFormGroup;

  signatureConfig: SignatureConfig | undefined;
  usingDocusignTemplate = false;

  recipientInfoService: IRecipientInfoService;

  showEmailOption = false;
  showAiContentNotice = false;
  readonly showDocumentOptions: Signal<Boolean> = computed(() => {
    if (!this.featureFlagService.enabled(FeatureFlagService.FLAGS.directOutputFileSave)()) {
      return true;
    }
    const isDocusignEnabledTemplate = this.signatureConfig?.provider === 'docusign' && this.signatureConfig?.enabled;
    return !isDocusignEnabledTemplate || (this.signatureConfig?.properties?.directOutputFileSave ?? true);
  });

  entitiesMeta: IEntityMetadata[];

  originalFilename: string;

  outputFilenameFormGroup: FormGroup;
  outputFilenameHasError = false;
  outputFilenameErrorMessage: string | undefined = undefined;
  outputFilenameErrorTooltipMessage: string | undefined = undefined;

  private readonly iconService: IIconService;
  private readonly workflowService: IWorkflowService;
  private readonly terminologyService: ITerminologyService;
  private readonly composeMailService: IComposeMailService;
  private static DEFAULT_OUTPUT_FORMAT = 'docx';
  private subscriptions: Subscription = new Subscription();
  private serviceProvider: ISharedServiceProviderService;

  private readonly SignatureEnvelopeValidations = {
    docusign: {
      recipient: {
        email: { maxLength: 100 },
        name: { maxLength: 100 },
      },
      emailSubject: { maxLength: 100 },
      emailBody: { maxLength: 10000 },
    },
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IAdActionsDialogData,
    private utilsService: UtilsService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AdActionsDialogComponent>,
    private messageNotificationService: MessageNotificationService,
    private fileListControllerService: FileListControllerService,
    private docusignConsentService: DocusignConsentService,
    private identityStorage: IdentityStorage,
    private adv2ConfigService: Adv2ConfigService,
    private serviceProviderService: ServiceProviderService,
    private generateFilenameService: GenerateFilenameService,
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private pendoService: PendoService,
    private featureFlagService: FeatureFlagService,
    private clientConfigService: ClientConfigService,
    private currentUserOverrideState: CurrentUserOverrideStateService,
  ) {
    dialogRef.disableClose = true;
    const namespace = this.data.templateWorkflowConfig.clientNamespace;
    this.serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(namespace);
    this.iconService = this.serviceProviderService.getIconService(namespace);
    this.recipientInfoService = this.serviceProvider.getRecipientInfoService();
    this.workflowService = this.serviceProviderService.getWorkflowService(namespace);
    this.terminologyService = this.serviceProvider.getTerminologyService();
    this.composeMailService = this.serviceProviderService.getComposeMailService(namespace);
  }

  ngOnInit(): void {
    this.utilsService.dialogOpen(true);

    this.buildSaveToDatasourceText();
    this.buildSaveBtnText();

    this.signatureConfig = this.findSendForSignatureWorkflowConfig();
    if (this.signatureConfig) {
      this.initialiseSignatureSection();
      this.determineSignatureSpecificValues();
    }

    this.determineShowEmailOption();
    this.buildDocumentTypeTooltip();
    this.initOutputFilenameFormGroup();
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.utilsService.dialogOpen(false);
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * Whether the edit/preview envelope button wrapper should be shown.
   * When the editEnvelopeInDocusign feature flag is on, the wrapper is driven by the
   * config's editEnvelope value; otherwise it is always shown (legacy behaviour).
   * When this is false the send-envelope button expands to the full dialog width.
   */
  protected get showEnvelopeButtonWrapper(): boolean {
    return this.featureFlagService.enabled(FeatureFlagService.FLAGS.editEnvelopeInDocusign)()
      ? this.editEnvelope === true
      : true;
  }

  getIcon(type: string) {
    return this.iconService.getIcon(type);
  }

  get actingAs(): DocumentSender | null {
    return this.currentUserOverrideState.current;
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onEditClick(): void {
    this.actionInProgress = true;
    const editTask = new EditS3FileTask(
      this.data.options.key,
      this.messageNotificationService,
      this.editDocumentLocation,
      this.identityStorage.get().namespace,
      'DOC_CLIENT'
    );
    editTask.asyncOpenFileResponse().then((value) => {
      this.editDocumentLocation = value;
    });

    const taskRunner = new WorkflowTaskRunner(this.workflowService, editTask);
    this.subscriptions.add(
      taskRunner.run().subscribe({
        next: () => {
          this.messageNotificationService.info('Opening file');

          setTimeout(() => {
            // todo can we wait for user to close word doc
            this.actionInProgress = false;
          }, 3000);
        },
        error: (error) => {
          this.messageNotificationService.error('The document could not be opened', error);
          this.actionInProgress = false;
        },
      })
    );

    this.editTaskPendoTrackEvent(editTask);
  }

  onDocumentActionClick(action: DocumentAction): void {
    this.actionInProgress = true;

    const documentSaveSuccess = (entity: IEntity, entitySelection: EntitySelection, files: IUploadedFileInfo[]) => {
      if (action === DocumentAction.Email) {
        const recipients = this.getRecipientId(entity);
        this.composeMailService.launch(entity, entitySelection, files, this.buildSubject(files), recipients);
        this.messageNotificationService.success(
          `Document saved document to ${UtilsService.capitaliseFirstLetter(runtimeEnvironment.origin.namespace)}`
        );
      }
      this.actionInProgress = false;
    };

    const saveToEntityTask = new SaveToEntityTask(
      this.data.options.key,
      this.documentType.split(','),
      this.data.entity,
      this.data.entitySelection,
      this.fileListControllerService,
      this.messageNotificationService,
      this.utilsService,
      this.editDocumentLocation,
      this.outputFilenameFormGroup.get('outputFilename').value + '.docx',
      documentSaveSuccess
    );
    const taskRunner = new WorkflowTaskRunner(this.workflowService, saveToEntityTask);
    this.subscriptions.add(
      taskRunner.run().subscribe({
        error: (error) => {
          this.messageNotificationService.error('The workflow could not be executed', error);
          this.actionInProgress = false;
        },
      })
    );
    this.saveADToPendoTrackEvent();
  }

  async onSendEnvelopeClick(edit: boolean): Promise<void> {
    this.actionInProgress = true;

    const signatureConsentOk = await this.signatureConsentOk(this.signatureConfig.provider);
    if (!signatureConsentOk) {
      this.actionInProgress = false;
      return;
    }

    const signatureConfig: SignatureConfig = {
      accountId: this.signatureConfig.accountId,
      signingOrder: this.signatureConfig.signingOrder,
      ccFollowsSigningOrder: this.signatureConfig.ccFollowsSigningOrder,
      signedOutputFileType: this.signatureConfig.signedOutputFileType,
      combinedOutput: this.signatureConfig.combinedOutput,
      provider: this.signatureConfig.provider,
      emailSubject: this.formGroup.get('emailSubject').value,
      emailBody: this.formGroup.get('emailBody').value,
      recipients: this.getRecipientsFromControls(),
      documents: this.signatureConfig.documents,
      enabled: this.signatureConfig.enabled,
    };
    const status = edit ? SendStatus.CREATED : SendStatus.SENT;
    const sendForSignatureTask = new SendForSignatureTask(
      this.data.options.key,
      signatureConfig,
      status,
      this.data.entity,
      this.fileListControllerService,
      this.messageNotificationService,
      this.dialog,
      this.editDocumentLocation,
      this.outputFilenameFormGroup.get('outputFilename').value + '.docx',
      (envelopeUrl: string) => {
        this.envelopeUrl = envelopeUrl;
        this.envelopeCreatedTime = new Date().toLocaleTimeString([], { hour: 'numeric', minute: 'numeric', hour12: true });
      }
    );
    const taskRunner = new WorkflowTaskRunner(this.workflowService, sendForSignatureTask);
    this.subscriptions.add(
      taskRunner.run().subscribe({
        error: (error) => {
          this.messageNotificationService.error('The workflow could not be executed', error);
          this.actionInProgress = false;
        },
        complete: () => (this.actionInProgress = false),
      })
    );

    this.sendADPendoTrackEvent(edit);
  }

  onPreviewEnvelopeDraft(evelopeUrl: string) {
    window.open(evelopeUrl);
  }

  onOutputFileTypeToggleChange(e: MatButtonToggleChange): void {
    this.documentType = e.value;
    this.buildDocumentTypeTooltip();
  }

  getEntityLabel(type: string) {
    return this.entitiesMeta.find((e) => e.name.toLowerCase() === type.toLowerCase())?.label;
  }

  findEntityMeta(type: string) {
    return this.entitiesMeta?.find((e) => e.name.toLowerCase() === type?.toLowerCase());
  }

  checkFileName(fileName: string) {
    this.generateFilenameService
      .generateFilename(runtimeEnvironment.origin.namespace, fileName + '.docx')
      .subscribe((response: IGenerateFilenameResponse) => {
        if (response.warnings) {
          this.outputFilenameErrorMessage = response.warnings[0];
        } else {
          this.outputFilenameErrorMessage = undefined;
        }
        this.outputFilenameHasError = !!this.outputFilenameErrorMessage;
        this.outputFilenameErrorTooltipMessage = !!this.outputFilenameErrorMessage
          ? this.outputFilenameErrorMessage + '. Please edit before continuing.'
          : undefined;
      });
  }

  private editTaskPendoTrackEvent(editTask: EditS3FileTask) {
    const metaData = {
      recordID: this.data.entity.id,
      awesomeDocName: this.outputFilenameFormGroup.get('outputFilename')?.value,
      workflowName: editTask.workflowName,
      datasource: runtimeEnvironment.origin.namespace,
    };
    this.pendoService?.recordEvent('Preview and Edit button', metaData);
  }

  private saveADToPendoTrackEvent() {
    this.pendoService?.recordEvent('Save to Datasource', {
      recordID: this.data.entity.id,
      originalFilename: this.originalFilename,
      outputFilename: this.outputFilenameFormGroup.get('outputFilename')?.value,
      documentType: this.documentType,
      workflowName: 'SAVE_TO_ENTITY',
      entity: this.getEntityLabel(this.data.entity.type),
      datasource: runtimeEnvironment.origin.namespace,
    });
  }

  private sendADPendoTrackEvent(edit: boolean) {
    const pendoTrackEventName = edit ? 'Edit envelope in Docusign' : 'Send Envelope with Docusign';
    this.pendoService?.recordEvent(pendoTrackEventName, {
      recordID: this.data.entity.id,
      originalFilename: this.originalFilename,
      outputFilename: this.outputFilenameFormGroup.get('outputFilename')?.value,
      usingDocusignTemplate: this.usingDocusignTemplate,
      accountId: this.signatureConfig.accountId,
      signingOrder: this.signatureConfig.signingOrder,
      sendingOnBehalfOfOptions: this.signatureConfig.properties.sendingOnBehalfOfOptions?.type,
      ccFollowsSigningOrder: this.signatureConfig.ccFollowsSigningOrder,
      provider: this.signatureConfig.provider,
      emailSubjectCharLength: this.formGroup.get('emailSubject').value.length,
      emailBodyCharLength: this.formGroup.get('emailBody').value.length,
      numberOfRecipients: this.getRecipientsFromControls().length,
      documents: this.signatureConfig.documents,
      enabled: this.signatureConfig.enabled,
      documentType: this.documentType,
      workflowName: 'SEND_TO_SIGNATURE',
      entity: this.getEntityLabel(this.data.entity.type),
      datasource: runtimeEnvironment.origin.namespace,
    });
  }

  private buildDocumentTypeTooltip(): void {
    let suffix = '';
    if (this.documentType === 'docx' || this.documentType === undefined) {
      suffix = 'a Word Doc';
    } else if (this.documentType === 'pdf') {
      suffix = 'a PDF';
    } else {
      suffix = 'both a Word Doc and a PDF';
    }
    this.actions.forEach((a) => {
      a.tooltip = `${a.prefix}${suffix}`;
    });
  }

  private buildSubject(uploadedFileInfo: IUploadedFileInfo[]): string {
    const removeExtension = (fn: string) => {
      const dotIndex = fn.lastIndexOf('.');
      return dotIndex > -1 ? fn.substring(0, dotIndex) : fn;
    };

    const fileNameSet: Set<string> = new Set<string>();
    uploadedFileInfo.forEach((fileInfo) => {
      fileNameSet.add(removeExtension(fileInfo.fileName));
    });

    let subject = '';
    [...fileNameSet.values()].forEach((fileName, index) => {
      subject += fileName;
      if (index < fileNameSet.size - 1) {
        subject += ', ';
      }
    });

    return subject;
  }

  private getRecipientId(entity: IEntity): IRecipient[] {
    if (entity.type === BullhornEntityTypes.ClientContact) {
      return [{ id: parseInt(entity.id, 10) }];
    }
    return [];
  }

  private initialiseSignatureSection(): void {
    const recipientsForm = new UntypedFormArray([], [this.recipientsNotEmpty]);
    this.formGroup = new UntypedFormGroup({
      recipients: recipientsForm,
      emailSubject: new UntypedFormControl(this.signatureConfig.emailSubject, [Validators.required]),
      emailBody: new UntypedFormControl(this.signatureConfig.emailBody),
    });

    this.applySignatureProviderValidators(this.formGroup);

    // we need to run the validation because the CE will not create the form until the button is clicked
    if (runtimeEnvironment.runningAsChromeExtension) {
      this.buildRecipientsErrorTooltip(this.signatureConfig.recipients, this.signatureConfig.emailSubject, this.signatureConfig.emailBody);
      this.validConfig = !!!this.invalidRecipientDetails;
    }

    // monitor the value of the form and update as required
    this.subscriptions.add(
      this.formGroup.valueChanges.pipe(debounceTime(400)).subscribe((_) => {
        this.validConfig = this.formGroup.valid;
        if (!this.formGroup.valid) {
          const recipients = this.getRecipientsFromControls();
          const emailSubject = this.formGroup.get('emailSubject').value;
          const emailBody = this.formGroup.get('emailBody').value;
          this.buildRecipientsErrorTooltip(recipients, emailSubject, emailBody);
        }
      })
    );
  }

  private determineSignatureSpecificValues(): void {
    if (this.signatureConfig.provider === 'docusign' && this.signatureConfig.enabled) {
      this.usingDocusignTemplate = !!this.signatureConfig.properties.templateId;
    }
  }

  private findSendForSignatureWorkflowConfig(): SignatureConfig | undefined {
    return this.data.options.workflows.find((wf) => wf.workflow === 'SEND_TO_SIGNATURE')?.config?.config;
  }

  private buildSaveToDatasourceText(): void {
    const numberOfDestinations = this.data.entitySelection.destinations.length;

    let text;
    if (numberOfDestinations === 1) {
      text = `Save to the ${TermPlaceholder.NAMESPACE} ${TermPlaceholder.RECORD_NAME}`;
    } else {
      text = `Save to ${numberOfDestinations} ${TermPlaceholder.NAMESPACE} ${TermPlaceholder.RECORD_NAME_PLURAL}`;
    }

    this.saveToDatasourceText = TerminologySentenceBuilder.format(text, this.terminologyService);
  }

  private buildSaveBtnText(): void {
    const text = `Save to ${TermPlaceholder.NAMESPACE}`;
    const saveBtnAction = this.actions.find((action) => action.action === DocumentAction.Save);
    if (saveBtnAction) {
      saveBtnAction.title = TerminologySentenceBuilder.format(text, this.terminologyService);
    }
  }

  // TODO: Refactor into a customValidator if it makes sense
  private buildRecipientsErrorTooltip(recipients: Recipient[], emailSubject: string, emailBody: string): void {
    const missingFields = [];
    let recipientFieldsMissing = [];

    for (let i = 0; i < recipients.length; i++) {
      recipientFieldsMissing = [];

      if (!recipients[i].name) {
        recipientFieldsMissing.push('Name');
      }

      if (!UtilsService.isValidEmail(recipients[i].email)) {
        recipientFieldsMissing.push('Email');
      }

      if (recipientFieldsMissing.length > 0) {
        const missingFieldsString = recipientFieldsMissing.join(', ');
        const recipientString = `Recipient ${i + 1}: ${missingFieldsString}`;
        missingFields.push('\n', recipientString);
      }
    }

    if (recipients.length === 0) {
      missingFields.push('\n No recipients found');
    }

    if (!emailSubject) {
      missingFields.push('\n Email is missing a subject');
    }

    // TODO: BW - future move to a separate service
    // Do specific validations for Docusign
    if (this.signatureConfig.provider === 'docusign') {
      this.checkDocusignFields(missingFields, recipients, emailSubject, emailBody);
    }

    this.invalidRecipientDetails = missingFields.join(' ');
  }

  private checkDocusignFields(missingFields: string[], recipients: Recipient[], emailSubject: string, emailBody: string) {
    const validations = this.SignatureEnvelopeValidations.docusign; // for easier access

    recipients.forEach((recipient, i) => {
      if (recipient.email?.length > validations.recipient.email.maxLength) {
        missingFields.push(`\n Recipient ${i + 1}: Email exceeds ${validations.recipient.email.maxLength} character limit`);
      }
      if (recipient.name?.length > validations.recipient.name.maxLength) {
        missingFields.push(`\n Recipient ${i + 1}: Name exceeds ${validations.recipient.name.maxLength} character limit`);
      }
    });

    if (emailSubject?.length > validations.emailSubject.maxLength) {
      missingFields.push(`\n Email subject exceeds ${validations.emailSubject.maxLength} character limit`);
    }

    if (emailBody?.length > validations.emailBody.maxLength) {
      missingFields.push(`\n Email body exceeds ${validations.emailBody.maxLength} character limit`);
    }
  }

  private getRecipientsFromControls(): Recipient[] {
    const recipientControls = (this.formGroup.get('recipients') as UntypedFormArray).controls;
    return recipientControls.map((rc) => ({
      name: rc.get('name').value,
      email: rc.get('email').value,
      role: rc.get('role').value,
      editing: rc.get('editing').value,
    }));
  }

  private recipientsNotEmpty(control: UntypedFormArray): ValidationErrors | null {
    const recipients = control.value;
    if (!recipients || !recipients.length) {
      return { emptyRecipients: true };
    }
    return null;
  }

  private validateDocusignRecipientFields(control: UntypedFormArray): ValidationErrors | null {
    const recipients = control.value;
    for (let i = 0; i < recipients.length; i++) {
      const recipient = recipients[i];
      const { name, email } = recipient;
      if (name?.length > this.SignatureEnvelopeValidations.docusign.recipient.name.maxLength) {
        return { nameTooLong: true };
      }
      if (email?.length > this.SignatureEnvelopeValidations.docusign.recipient.email.maxLength) {
        return { emailTooLong: true };
      }
    }
    return null;
  }

  private applySignatureProviderValidators(formGroup: FormGroup): void {
    if (this.signatureConfig.provider === 'docusign') {
      formGroup.get('recipients').addValidators([this.validateDocusignRecipientFields.bind(this)]);
      formGroup
        .get('emailSubject')
        .addValidators([Validators.maxLength(this.SignatureEnvelopeValidations.docusign.emailSubject.maxLength)]);
      formGroup.get('emailBody').addValidators([Validators.maxLength(this.SignatureEnvelopeValidations.docusign.emailBody.maxLength)]);
    }
  }

  // Keyboard Shortcuts
  // - Use Escape to close the dialog
  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (!this.utilsService.escapableElementActive()) {
      if (event.code === 'Escape') {
        this.dialogRef.close();
        this.pendoService?.recordEvent('Escape key pressed - to close AD dialog');
      }
    } else {
      this.utilsService.escapableElementActive(false);
    }
  }

  private determineShowEmailOption(): void {
    // Very Bullhorn specific. Other valid URLs?

    try {
      // check for explicit argument
      const url = new URL(window.location.href);
      const composeMail = url.searchParams.get('composeMail');
      if (composeMail === 'bullhorn') {
        this.showEmailOption = true;
        return;
      }

      // Firefox does not support ancestorOrigins
      if (window.location.ancestorOrigins) {
        this.showEmailOption = window.location.ancestorOrigins.contains('https://app.bullhornstaffing.com');
      } else {
        this.showEmailOption = window.document.referrer.includes('https://app.bullhornstaffing.com');
      }
    } catch (error: any) {
      console.error('Failed to determine compose mail setting.', error);
      this.showEmailOption = false;
    }
  }

  private async signatureConsentOk(providerName: string): Promise<boolean> {
    if (providerName !== 'docusign') {
      console.warn(`No consent implementation for '${this.signatureConfig.provider}'`);
      return true;
    }

    const showOverlay = (consentProgress: SignatureConsentProgress) => {
      this.showOverlay = consentProgress === SignatureConsentProgress.START;
    };

    return await this.docusignConsentService.checkConsent(this.signatureConfig, showOverlay);
  }

  private initOutputFilenameFormGroup() {
    const filteredWorkflows: Workflow[] = this.data.options.workflows.filter((x) => x.config?.hasOwnProperty(SectionType.OutputFileName));
    if (filteredWorkflows.length === 0) {
      return;
    }

    const { originalFilename, outputFilename }: OutputFilename = filteredWorkflows[0].config[SectionType.OutputFileName] as OutputFilename;
    // We assume here that the file extension is .docx. If this changes we will need to modify this.
    const trimmedFilename = originalFilename.endsWith('.docx')
      ? originalFilename.substring(0, originalFilename.length - 5)
      : originalFilename;
    this.outputFilenameFormGroup = new FormGroup({
      outputFilename: new FormControl(trimmedFilename, [Validators.required]),
    });
    if (originalFilename !== outputFilename) {
      this.checkFileName(originalFilename);
    }

    this.originalFilename = trimmedFilename;
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.clientConfigService.isAiAssistEnabled().subscribe((enabled) => {
        this.showAiContentNotice = enabled;
      })
    );
    this.subscriptions.add(
      this.serviceProvider
        .getEntityMetadataService()
        .getEntitiesMetadata()
        .subscribe((entities) => (this.entitiesMeta = entities))
    );
    this.subscriptions.add(
      this.adv2ConfigService
        .getConfig()
        .subscribe((config) => {
          this.documentType = config?.config?.outputFormat ?? AdActionsDialogComponent.DEFAULT_OUTPUT_FORMAT;
          if (this.featureFlagService.enabled(FeatureFlagService.FLAGS.editEnvelopeInDocusign)()) {
            this.editEnvelope = config?.signature?.properties?.editEnvelope ?? true;
          }
        }
    ));
    this.subscriptions.add(
      this.outputFilenameFormGroup
        .get('outputFilename')
        .valueChanges.pipe(debounceTime(300))
        .subscribe((fileName) => {
          this.checkFileName(fileName);
        })
    );
  }
}
